import { isBrowser } from '@/utils/environment';
import React from 'react';

interface WindowWithOpenComponentHistory extends Window {
  // Keep an ordered list of all components with back button overrides so that
  // we can programmatically close the component when the back button is pressed
  openedComponentKeys: number[];
}

declare const window: WindowWithOpenComponentHistory;

export default function useBackButtonOverride(
  isOpen: boolean,
  onClose: () => void = null,
  shouldCloseOnBack = true,
  onDisabledBackClick: () => void = null
): () => void {
  const [popStateKey, _setPopStateKey] = React.useState<number>(0);
  // In order to access popStateKey in an event listener, need to keep a reference to it
  const popStateKeyRef = React.useRef(popStateKey);
  const setPopStateKey = key => {
    popStateKeyRef.current = key;
    _setPopStateKey(key);
  };
  const [isDisabled, _setIsDisabled] = React.useState<boolean>(false);
  const isDisabledRef = React.useRef(isDisabled);

  const setIsDisabled = key => {
    isDisabledRef.current = key;
    _setIsDisabled(key);
  };

  const isCurrentComponent = (): boolean =>
    popStateKeyRef.current &&
    window.openedComponentKeys &&
    window.openedComponentKeys[window.openedComponentKeys.length - 1] === popStateKeyRef.current;

  // this function is used to disable onClose firing twice
  // e.g. a modal is closed using the "X" button on the modal and the onClose function is already fired
  // we do not want to fire onClose again because it will create a duplicate event.
  const disableBackButtonOverride = () => {
    setIsDisabled(true);
  };

  React.useEffect(() => {
    // push to the history so that when the browser back button is pressed we can close the component instead
    if (isBrowser() && window.history && window.history.pushState) {
      if (isOpen) {
        setIsDisabled(false);
        const newPopStateKey = Date.now();
        setPopStateKey(newPopStateKey);

        window.openedComponentKeys = window.openedComponentKeys || [];
        window.openedComponentKeys.push(newPopStateKey);

        // console.log('Pushing window history: ', newPopStateKey);
        window.history.pushState(newPopStateKey, null, `#${newPopStateKey}`);
        window.addEventListener('popstate', handlePopState);
      } else {
        // only remove the history if the modal was closed manually (otherwise it would have been removed on popstate)
        if (isCurrentComponent()) {
          // console.log('history back', popStateKeyRef.current);
          window.history.back();
        } else {
          // console.log('isClosed: Not current component: ', popStateKeyRef.current);
        }
        window.removeEventListener('popstate', handlePopState);
      }
    }
  }, [isOpen]);

  function handlePopState() {
    if (isCurrentComponent()) {
      // console.log('POPSTATE Is current component', popStateKeyRef.current);
      if (shouldCloseOnBack) {
        // console.log('should close on back');
        window.openedComponentKeys = window.openedComponentKeys.filter(key => key !== popStateKeyRef.current);
        if (onClose) {
          if (!isDisabledRef.current) {
            onClose();
          }
        }
      } else {
        // console.log('Re-push history');
        // the state was popped, push it again (because we have disabled the back button and do not want the user to be able to close the modal)
        // NB: only seems to work if the page has fully loaded
        window.history.pushState(popStateKeyRef.current, null, `#${popStateKeyRef.current}`);
        if (onDisabledBackClick) {
          onDisabledBackClick();
        }
      }
    } else {
      // console.log('POPSTATE Not current component', popStateKeyRef.current);
    }
  }

  return disableBackButtonOverride;
}
